<template>
  <AccordionSection
    :id="item?.fields.id ?? `section-${item.sys.id}`"
    :color-mode="item.fields.theme"
    :open-multiple-items="item.fields.openMultipleAccordionItems"
    :item-heading-level="level"
    v-bind="
      $ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'headline'
      })
    "
  >
    <Headline v-if="item.fields.headline" :item="item.fields.headline" />
    <MappedComponents
      v-if="props.item.fields.customContent"
      :items="props.item.fields.customContent"
    />
    <AccordionItem
      v-for="{ fields, sys } in accordionItems"
      :key="sys.id"
      :is-open="fields.initiallyOpened"
    >
      <template #title>{{ fields.title }}</template>
      <MappedComponents :items="fields.contentSlot ?? []" />
    </AccordionItem>
  </AccordionSection>
</template>

<script setup lang="ts">
import {
  AccordionSection,
  AccordionItem
} from '@hypercodestudio/basler-components';
import Headline from './Headline.vue';
import { mapHeadlineLevel } from '~/utils/mapHeadlineLevel';
import type {
  IAccordionSection,
  IAccordionSectionItem
} from '~~/lib/ContentfulService';
import type { FAQPage, WithContext } from 'schema-dts';
import {
  createHtmlRenderOptions,
  convertContentSlotToHtml
} from '~/utils/richtext/convertContentSlotToHtml';
const { $ContentfulLivePreview } = useNuxtApp();

interface Props {
  item: IAccordionSection;
}

const props = defineProps<Props>();

const level = computed(() => mapHeadlineLevel(props.item.fields.headingMarkup));

/** Preview/locale drafts can omit `accordionItems`; never call `.map` on undefined. */
const accordionItems = computed(() => props.item.fields.accordionItems ?? []);

if (props.item.fields.useFaqSchema) {
  // Create render options and FAQ page schema
  const htmlRenderOptions = createHtmlRenderOptions();

  const faqPage = computed<WithContext<FAQPage>>(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: accordionItems.value.map((item: IAccordionSectionItem) => ({
      '@type': 'Question',
      name: item.fields.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: convertContentSlotToHtml(
          item.fields.contentSlot,
          htmlRenderOptions
        )
      }
    }))
  }));

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(faqPage.value),
        tagPriority: -1 // Optional: Sicherstellen, dass es zuletzt eingefügt wird
      }
    ]
  });
}
</script>

<style>
.tab-panel__content .accordion-section {
  padding-block: 0px;
}

.tab-panel__content .accordion-section__inner {
  padding: 0px;
}
</style>
